import React, { createContext, useState, useContext, useEffect } from 'react';
import { getMe } from 'service/getMe';
import { IUserList } from 'models/users';
import noti from 'libs/notification';
import { getCookie, setCookie, removeCookie } from 'utils/cookies';
import { useLocation, useNavigate } from 'react-router-dom';
import Routers from 'constants/routers';
import { ClearSessionOnRouteChange } from 'pages/Candidates/components/hooks/useRestoreOverviewState';

interface AuthContextType {
  user: IUserList | null;
  isAuthenticated: boolean;
  isLoadingAuth: boolean;
  login: (token: string) => void;
  logout: () => void;
  setUser: (user: IUserList | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<IUserList | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true)
  const navi = useNavigate();
  const location = useLocation();
  const login = async (token: string) => {
    setCookie('token', token, 1, { path: '/' });
    setIsAuthenticated(true);
    try {
      const userData = await getMe(token);

      localStorage.setItem('currentUserId', userData.id.toString());
      localStorage.setItem('forceLogout', Date.now().toString());
      sessionStorage.setItem('active', 'true');

      if (userData.role === 'admin') {
        setUser(userData);
        noti('success', 'Login successfully');
        const from = Routers.Jobs;
        navi(from);
      } else if (['board_of_directors', 'chief_operating_officer'].includes(userData.role)) {
        setUser(userData);
        noti('success', 'Login successfully');
        const from = Routers.BODInterviewManagement;
        navi(from);
      } else if (userData.role === 'human_resource') {
        setUser(userData);
        noti('success', 'Login successfully');
        const from = Routers.Jobs;
        navi(from);
      } else if (userData.role === 'training_coordinator') {
        setUser(userData);
        noti('success', 'Login successfully');
        const from = Routers.Training;
        navi(from);
      } else if (userData.role === 'user') {
        noti('error', 'You can not login to admin site!');
        logout();
      } else if (userData.role === 'leader') {
        setUser(userData);
        noti('success', 'Login successfully');
        const from = Routers.Candidates;
        navi(from);
      }
      else {
        noti('error', 'Invalid account. Contact admin to receive support!');
      }
    } catch (error) {
      noti('error','Error fetching user data');
    }
  };

  const logout = () => {
    removeCookie('token');
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('currentUserId');
  };
  // Fetch user data based on token
  const fetchUserData = async (token: string, isFreshLogin = false) => {
    try {
      const userData = await getMe(token);

      if (isFreshLogin) {
        noti('success', 'Login successfully');
      }

      if (userData.role === 'admin') {
        setUser(userData);
      } else if (['board_of_directors', 'chief_operating_officer'].includes(userData.role)) {
        setUser(userData);
      } else if (userData.role === 'human_resource') {
        setUser(userData);
      } else if (userData.role === 'training_coordinator') {
        setUser(userData);
      } else if (userData.role === 'leader') {
        setUser(userData);
      } else if (userData.role === 'user') {
        noti('error', 'You can not login to admin site!');
        logout();
      } else {
        logout();
        noti('error', 'Invalid account. Contact admin to receive support!');
      }
    } catch (error) {
      noti('error','Error fetching user data');
      logout();
    }
  };

  useEffect(() => {
    const token = getCookie('token');
    if (token && !user) {
      setIsAuthenticated(true);
      fetchUserData(token).finally(() => setIsLoadingAuth(false));
    } else {
      setIsLoadingAuth(false);
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('active');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'logout') {
        // Logout
        removeCookie('token');
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/login';
      } else if (event.key === 'forceLogout') {
        // Check login different user 
        const currentUserId = localStorage.getItem('currentUserId');
        if (user && user.id?.toString() !== currentUserId) {
          // login with different userId → logout old tab
          window.location.href = '/login';
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  //clear session 
  ClearSessionOnRouteChange();

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoadingAuth, login, logout, setUser }}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
